// The phone's tabs keep your place (UX-39, B-310, DIRECTION U20).
//
// An app's tab bar keeps each tab where it was left: its screen, how far down
// it was scrolled, what was typed into its search (Apple's tab bars:
// switching "while preserving the current navigation state within each
// section"; Android keeps a back stack per tab). The site's tabs forgot all
// of it: League three screens deep, then My Team, then League again, was the
// League page at its top (2026-10-13).
//
// Three memories, all for this page's life only — a reload starts fresh, as
// an app does after it was closed:
//
//   WHICH SCREEN each tab was last on, per league (`rememberTab`/`recallTab`).
//   HOW FAR DOWN each screen was scrolled (`useScrollMemory`): by history
//     entry for Back, and by address for a tab tapped back to.
//   WHAT A SCREEN WAS SET TO (`useRemembered`): Players' search and filters,
//     the matchup's segment.
//
// No React Router state and nothing in the address: none of it is somewhere a
// link should take somebody else.

import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

export type TabId = 'team' | 'matchup' | 'draft' | 'league' | 'players' | 'calendar';

/** The tab a path inside a league belongs to; `groups` is the League tab's paths. */
export function tabOf(suffix: string, groups: readonly string[]): TabId | null {
  if (suffix === '' || suffix === '/') return 'team';
  if (suffix.startsWith('/matchup')) return 'matchup';
  if (suffix.startsWith('/draft') && !suffix.startsWith('/draft-recap')) return 'draft';
  // Compare is picked from Players and goes back there (B-407).
  if (suffix.startsWith('/players') || suffix.startsWith('/compare')) return 'players';
  if (suffix.startsWith('/calendar')) return 'calendar';
  if (groups.some((p) => suffix.startsWith(p))) return 'league';
  return null;
}

const lastScreen = new Map<string, string>();
/** Whose places these are: another account starts with none (see `forAccount`). */
let account: string | null = null;

/** This tab was last on `to` (path and query) in the league at `base`. */
export function rememberTab(base: string, tab: TabId, to: string): void {
  lastScreen.set(`${base}|${tab}`, to);
}

export function recallTab(base: string, tab: TabId): string | null {
  return lastScreen.get(`${base}|${tab}`) ?? null;
}

// ── Scroll ─────────────────────────────────────────────────────────────────

const byEntry = new Map<string, number>();
const byAddress = new Map<string, number>();
/** The address a tab tap is about to open, whose scroll comes back with it. */
let returning: string | null = null;

/** The next screen at `to` is a tab tapped back to: give it its scroll. */
export function expectTabReturn(to: string): void {
  returning = to;
}

/** What scrolls a phone's screen: the screen in flow (UX-38), or a dashboard's widgets. */
export function screenScroller(main: HTMLElement | null): HTMLElement | null {
  return main?.querySelector<HTMLElement>('.fsp-flow, .fsp-widgets') ?? null;
}

/**
 * Remember each screen's scroll and give it back: on Back to the entry it was
 * left at, on a tab tapped back to the address. A screen opened any other way
 * starts at its top, as a newly pushed one does in an app.
 */
export function useScrollMemory(main: RefObject<HTMLElement | null>, on: boolean): void {
  const location = useLocation();
  const type = useNavigationType();
  const address = location.pathname + location.search;
  const here = useRef({ key: location.key, address });
  here.current = { key: location.key, address };

  useLayoutEffect(() => {
    const el = main.current;
    if (!on || el === null) return;
    const save = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || target !== screenScroller(el)) return;
      byEntry.set(here.current.key, target.scrollTop);
      byAddress.set(here.current.address, target.scrollTop);
    };
    el.addEventListener('scroll', save, { capture: true, passive: true });
    return () => el.removeEventListener('scroll', save, { capture: true });
  }, [main, on]);

  useLayoutEffect(() => {
    if (!on) return;
    const tabReturn = returning === address;
    returning = null;
    const top = type === 'POP' ? byEntry.get(location.key) : tabReturn ? byAddress.get(address) : 0;
    if (top === undefined || top === 0) return;
    // The screen draws its held answer at once (readStore), but a list may
    // need a frame or two to reach its height: try for about half a second.
    let frames = 0;
    let handle = 0;
    const restore = () => {
      const sc = screenScroller(main.current);
      if (sc !== null && sc.scrollHeight - sc.clientHeight >= top) {
        sc.scrollTop = top;
        return;
      }
      if (frames++ < 30) handle = requestAnimationFrame(restore);
      else if (sc !== null) sc.scrollTop = top;
    };
    restore();
    return () => cancelAnimationFrame(handle);
  }, [location.key]);
}

/** Tapping the tab you are on, at its first screen: back to the top. */
export function scrollToTop(main: HTMLElement | null): void {
  const sc = screenScroller(main);
  if (sc === null) return;
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  sc.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
}

// ── A screen's own settings ────────────────────────────────────────────────

const held = new Map<string, unknown>();

/**
 * Hold these for this account. Another account signing in on the same page —
 * two people on one phone — starts with no screen, scroll or search of the
 * last one's (the review of 2026-10-13); signing out keeps the page, so this
 * is the place that has to say so.
 */
export function forAccount(id: string | null): void {
  if (id === account) return;
  account = id;
  lastScreen.clear();
  byEntry.clear();
  byAddress.clear();
  held.clear();
  returning = null;
}

/**
 * `useState` that a tab keeps: the value outlives the screen leaving and comes
 * back with it, for this page's life. `key` names the screen and the league.
 */
export function useRemembered<T>(
  key: string,
  initial: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const recall = (k: string) => (held.has(k) ? (held.get(k) as T) : initial);
  const [state, setState] = useState(() => ({ key, value: recall(key) }));
  // The same screen drawn for another league starts from that league's own.
  const value = state.key === key ? state.value : recall(key);
  const keyRef = useRef(key);
  keyRef.current = key;
  const initialRef = useRef(initial);
  initialRef.current = initial;
  const set = useCallback((next: T | ((prev: T) => T)) => {
    setState((prev) => {
      const k = keyRef.current;
      const before =
        prev.key === k ? prev.value : held.has(k) ? (held.get(k) as T) : initialRef.current;
      const v = typeof next === 'function' ? (next as (p: T) => T)(before) : next;
      held.set(k, v);
      return { key: k, value: v };
    });
  }, []);
  return [value, set];
}
